// ErrorInlineWithRetry.tsx — M2.1 NEW · 替代 toast for 7 wire error codes.
//
// FE_SPEC §6.1 + TEST_PLAN §2-C (5 variants). Source codes per
// docs/contracts/m2/error_codes.m2.yaml + codes_registered_v2_1.
//
// 7 codes covered (5 dialog · 2 contextual):
//   - storage_pressure        (flat · 503 · retry button)
//   - topic_not_applicable    (flat-hint · 400 · inline replace)
//   - attachment_too_large    (flat · 400 · inline)
//   - attachment_too_many     (flat · 400 · inline)
//   - content_filter_blocked  (nested-no-charge · 400 · "不会扣费" banner)
//   - url_unsafe              (inline-below-input · 400 · below URL input)
//   - session_aborted         (banner-aborted · live page · "不会扣费")

import { useTranslation } from 'react-i18next';

export type ErrorInlineCode =
  | 'storage_pressure'
  | 'topic_not_applicable'
  | 'attachment_too_large'
  | 'attachment_too_many'
  | 'content_filter_blocked'
  | 'url_unsafe'
  | 'session_aborted';

export interface ErrorInlineWithRetryProps {
  code: ErrorInlineCode;
  /** Trace id for support reference · displayed muted in footer. */
  trace_id?: string;
  /** Callback when retry button is clicked. Only rendered when retry is allowed. */
  onRetry?: () => void;
  /** Optional class for layout integration (e.g. 'mb-4' inside dialog). */
  className?: string;
}

const RETRY_ALLOWED: ReadonlyArray<ErrorInlineCode> = ['storage_pressure'];
const NO_CHARGE_BANNER: ReadonlyArray<ErrorInlineCode> = [
  'content_filter_blocked',
  'session_aborted',
];

/**
 * Resolves which i18n key + variant to use for a given code.
 * Centralized so tests can verify the mapping table directly.
 */
export function variantForCode(code: ErrorInlineCode): {
  i18nKey: string;
  variant: 'flat' | 'flat-hint' | 'nested-no-charge' | 'inline-below-input' | 'banner-aborted';
  hintI18nKey?: string;
} {
  switch (code) {
    case 'storage_pressure':
      return { i18nKey: 'error.session.storage_pressure', variant: 'flat' };
    case 'topic_not_applicable':
      return {
        i18nKey: 'error.session.topic_not_applicable.title',
        variant: 'flat-hint',
        hintI18nKey: 'error.session.topic_not_applicable.hint',
      };
    case 'attachment_too_large':
      return { i18nKey: 'error.session.attachment_too_large', variant: 'flat' };
    case 'attachment_too_many':
      return { i18nKey: 'error.session.attachment_too_many', variant: 'flat' };
    case 'content_filter_blocked':
      return { i18nKey: 'error.session.content_filter_blocked', variant: 'nested-no-charge' };
    case 'url_unsafe':
      return { i18nKey: 'error.session.url_unsafe', variant: 'inline-below-input' };
    case 'session_aborted':
      return { i18nKey: 'error.orchestration.session_aborted', variant: 'banner-aborted' };
  }
}

export default function ErrorInlineWithRetry({
  code,
  trace_id,
  onRetry,
  className = '',
}: ErrorInlineWithRetryProps) {
  const { t } = useTranslation();
  const { i18nKey, variant, hintI18nKey } = variantForCode(code);
  const showRetry = onRetry !== undefined && RETRY_ALLOWED.includes(code);
  const showNoCharge = NO_CHARGE_BANNER.includes(code);

  const role = variant === 'banner-aborted' ? 'alert' : 'alert';
  const ariaLive = variant === 'banner-aborted' ? 'assertive' : 'polite';

  const baseClass =
    variant === 'banner-aborted'
      ? 'border-vermilion-300 bg-vermilion-50 text-vermilion-900 px-4 py-3 rounded-md font-serif'
      : variant === 'inline-below-input'
        ? 'text-vermilion-700 text-sm mt-1 font-mono'
        : 'border border-vermilion-300 bg-paper text-vermilion-900 px-3 py-2 rounded-md font-serif';

  return (
    <div
      role={role}
      aria-live={ariaLive}
      data-testid="error-inline-with-retry"
      data-code={code}
      data-variant={variant}
      className={`${baseClass} ${className}`.trim()}
    >
      <div className="font-medium" data-testid="error-message">
        {t(i18nKey)}
      </div>

      {hintI18nKey && (
        <div className="mt-1 text-sm text-mist-700 font-serif" data-testid="error-hint">
          {t(hintI18nKey)}
        </div>
      )}

      {showNoCharge && (
        <div
          className="mt-2 text-xs text-gold-700 font-mono"
          data-testid="error-no-charge-banner"
        >
          ✓ {t('common.no_charge_applied', { defaultValue: '不会扣费' })}
        </div>
      )}

      {showRetry && (
        <div className="mt-3">
          <button
            type="button"
            onClick={onRetry}
            data-testid="error-retry-btn"
            className="text-sm text-mist-900 hover:text-vermilion-700 underline font-mono"
          >
            {t('common.retry', { defaultValue: '重试' })}
          </button>
        </div>
      )}

      {trace_id && (
        <div
          className="mt-2 text-xs text-mist-500 font-mono"
          data-testid="error-trace-id"
        >
          trace: {trace_id}
        </div>
      )}
    </div>
  );
}
